import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { API_BASE } from '../config';

/**
 * SupportChat — AI Support Bot slide-out panel (#39)
 *
 * Persistent chat history (learning companion). Personalized with icon + name.
 * Context-aware: knows which page/tab the user is on.
 * Slide-out panel from right side, full height.
 */
function SupportChat({ fetchWithAuth, currentPage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [botIcon, setBotIcon] = useState('🧠');
  const [botName, setBotName] = useState('AI Assistant');
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  // Load personalization on mount (so header icon/name are correct immediately)
  useEffect(() => {
    loadPersonalization();
  }, []);

  // Load suggestions on first open (history only when toggled ON)
  useEffect(() => {
    if (isOpen && !historyLoaded) {
      loadSuggestions();
      setHistoryLoaded(true);
    }
  }, [isOpen]);

  // Load/clear history when toggle changes
  useEffect(() => {
    if (showHistory) {
      loadHistory();
    } else {
      // Keep only current session messages (no IDs = current session)
      setMessages(prev => prev.filter(m => !m.id));
    }
  }, [showHistory]);

  // Update suggestions when page changes
  useEffect(() => {
    if (isOpen) {
      loadSuggestions();
    }
  }, [currentPage]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  const loadPersonalization = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/support/personalize`);
      if (res.ok) {
        const data = await res.json();
        setBotIcon(data.icon || '🧠');
        setBotName(data.name || 'AI Assistant');
      }
    } catch (err) { /* non-critical */ }
  };

  const loadHistory = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/support/history?limit=20`);
      if (res.ok) {
        const data = await res.json();
        if (data.history && data.history.length > 0) {
          const histMsgs = [];
          // Load in chronological order (API returns newest first)
          for (const h of data.history.reverse()) {
            histMsgs.push({ role: 'user', text: h.user_message, id: h.id });
            histMsgs.push({ role: 'bot', text: h.bot_response, id: h.id, helpful: h.helpful });
          }
          setMessages(histMsgs);
        }
      }
    } catch (err) { /* non-critical */ }
  };

  const loadSuggestions = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/support/suggestions?page_context=${encodeURIComponent(currentPage || 'Dashboard')}`);
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (err) { /* non-critical */ }
  };

  const sendMessage = async (text) => {
    const question = text || input.trim();
    if (!question || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: question }]);
    setLoading(true);

    try {
      const res = await fetchWithAuth(`${API_BASE}/api/support/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          page_context: currentPage || 'Unknown page',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, {
          role: 'bot',
          text: data.answer,
          queryId: data.query_id,
          confidence: data.confidence,
          sources: data.sources,
          resolutionType: data.resolution_type,
          helpful: null,
        }]);
        if (data.suggested_questions?.length > 0) {
          setSuggestions(data.suggested_questions);
        }
      } else {
        setMessages(prev => [...prev, {
          role: 'bot',
          text: "I'm having trouble connecting right now. Please try again in a moment.",
          helpful: null,
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'bot',
        text: "I'm having trouble connecting right now. Please try again in a moment.",
        helpful: null,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const acceptExtension = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/support/extend-limit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, {
          role: 'bot',
          text: `Great — you've got 5 more questions today! Your new limit is ${data.new_limit}. How can I help?`,
        }]);
      }
    } catch (err) { /* non-critical */ }
  };

  const submitFeedback = async (queryId, helpful) => {
    if (!queryId) return;

    // Update local state immediately
    setMessages(prev => prev.map(m =>
      m.queryId === queryId ? { ...m, helpful } : m
    ));

    try {
      await fetchWithAuth(`${API_BASE}/api/support/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query_id: queryId, helpful }),
      });
    } catch (err) { /* non-critical */ }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Render modal via portal to document.body
  const panelPortal = isOpen ? createPortal(
    <>
      <div className="sc-overlay" onClick={() => setIsOpen(false)} />
      <div className="sc-panel sc-panel--open" ref={panelRef}>
        {/* Modal Header */}
        <div className="sc-panelHeader">
          <div className="sc-panelTitle">
            <span className="sc-panelIcon">{botIcon}</span>
            <span>{botName}</span>
          </div>
          <div className="sc-historyGroup">
            <span className="sc-historyTitle">View History</span>
            <div className="sc-toggleRow">
              <span className={`sc-toggleLabel ${!showHistory ? 'sc-toggleLabel--active' : ''}`}>Off</span>
              <button
                className={`sc-toggle ${showHistory ? 'sc-toggle--on' : ''}`}
                onClick={() => setShowHistory(!showHistory)}
                role="switch"
                aria-checked={showHistory}
              >
                <span className="sc-toggleKnob" />
              </button>
              <span className={`sc-toggleLabel ${showHistory ? 'sc-toggleLabel--active' : ''}`}>On</span>
            </div>
          </div>
          <button className="sc-closeBtn" onClick={() => setIsOpen(false)} title="Close">×</button>
        </div>

        {/* Suggested Questions */}
        {suggestions.length > 0 && messages.length === 0 && (
          <div className="sc-suggestions">
            <span className="sc-suggestLabel">Suggested questions:</span>
            {suggestions.map((s, i) => (
              <button
                key={i}
                className="sc-suggestChip"
                onClick={() => sendMessage(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Messages */}
        <div className="sc-messages">
          {messages.length === 0 && !loading && (
            <div className="sc-welcome">
              <span className="sc-welcomeIcon">{botIcon}</span>
              <p>Hi! I'm <strong>{botName}</strong>, your Healist support assistant.</p>
              <p>Ask me anything about using the platform — features, settings, navigation, account management, and more.</p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`sc-msg sc-msg--${msg.role}`}>
              {msg.role === 'bot' && (
                <span className="sc-msgAvatar">{botIcon}</span>
              )}
              <div className={`sc-msgBubble sc-msgBubble--${msg.role}`}>
                <div className="sc-msgText" dangerouslySetInnerHTML={{
                  __html: msg.text
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\n/g, '<br />')
                }} />

                {/* Extension offer button */}
                {msg.role === 'bot' && msg.resolutionType === 'offer_extension' && (
                  <button className="sc-extendBtn" onClick={acceptExtension}>
                    Yes, extend my limit!
                  </button>
                )}

                {/* Feedback thumbs — bot messages only */}
                {msg.role === 'bot' && msg.queryId && (
                  <div className="sc-feedback">
                    <span className="sc-feedbackLabel">Helpful?</span>
                    <button
                      className={`sc-feedbackBtn ${msg.helpful === true ? 'sc-feedbackBtn--active' : ''}`}
                      onClick={() => submitFeedback(msg.queryId, true)}
                      title="Yes, helpful"
                    >👍</button>
                    <button
                      className={`sc-feedbackBtn ${msg.helpful === false ? 'sc-feedbackBtn--active' : ''}`}
                      onClick={() => submitFeedback(msg.queryId, false)}
                      title="Not helpful"
                    >👎</button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {loading && (
            <div className="sc-msg sc-msg--bot">
              <span className="sc-msgAvatar">{botIcon}</span>
              <div className="sc-msgBubble sc-msgBubble--bot sc-typing">
                <span className="sc-dot"></span>
                <span className="sc-dot"></span>
                <span className="sc-dot"></span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Follow-up suggestions after conversation */}
        {suggestions.length > 0 && messages.length > 0 && !loading && (
          <div className="sc-followups">
            {suggestions.map((s, i) => (
              <button
                key={i}
                className="sc-followupChip"
                onClick={() => sendMessage(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="sc-inputArea">
          <textarea
            ref={inputRef}
            className="sc-input"
            placeholder="Ask a question..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={loading}
          />
          <button
            className="sc-sendBtn"
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            title="Send"
          >
            ➤
          </button>
        </div>

      </div>
    </>,
    document.body
  ) : null;

  return (
    <>
      {/* Header Button — centered in header bar */}
      <button
        className="sc-headerBtn"
        onClick={() => setIsOpen(!isOpen)}
        title={`${botName} — Support Assistant`}
      >
        <span className="sc-headerIcon">{botIcon}</span>
        <span className="sc-headerName">{botName}</span>
      </button>

      {/* Panel rendered via portal to document.body */}
      {panelPortal}
    </>
  );
}

export default SupportChat;
